import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation, Outlet } from "react-router-dom";
import Reviews from "./Reviews";
import Login from "./Login";
import Users from "./Users";
import Restaurant from "./Restaurant";
import CampaignAnalytics from "./CampaignAnalytics";
import { HomeIcon, UsersIcon, BuildingStorefrontIcon, UserPlusIcon, ChartBarIcon, QuestionMarkCircleIcon, Bars3Icon, XMarkIcon, ArrowPathIcon, QrCodeIcon } from "@heroicons/react/24/outline";
import Profile from "./Profile";
import Stats from "./Stats";
import Invites from "./Invites";
import AcceptInvite from "./AcceptInvite";
import DeleteManagerModal from "./DeleteManagerModal";
import QRLanding from "./QRLanding";
import Privacy from "./Privacy";
import ForgotPassword from "./ForgotPassword";
import ResetPassword from "./ResetPassword";
import GoogleAuthCallback from "./GoogleAuthCallback";
import TwoFactorSetup from "./TwoFactorSetup";
import Register from "./Register";
import SignupRequests from "./SignupRequests";
import Billing from "./Billing";
import MasterDashboard from "./MasterDashboard";
import AuditLog from "./AuditLog";
import AiUsage from "./AiUsage";
import { apiFetch, apiAssetUrl, isSignedIn, logout } from "./api";
import { useTranslation } from "react-i18next";
import LanguageToggle from "./LanguageToggle";
import Help from "./Help";
import { InboxStackIcon, ShieldCheckIcon, CreditCardIcon, Squares2X2Icon, SparklesIcon } from "@heroicons/react/24/outline";
import { RestaurantProvider, useRestaurant } from "./RestaurantContext";
import { useAppUpdate, isStandalone } from "./useAppUpdate";
import ReadOnlyBanner from "./ReadOnlyBanner";
import GoogleAccessBanner from "./GoogleAccessBanner";
import RestaurantSwitcher from "./RestaurantSwitcher";
import { useNewReviewCount } from "./newReviews";

// The session cookie is httpOnly and unreadable here by design; this flag is
// only a render hint, and a stale one just yields a shell that 401s.
const isAuthenticated = () => isSignedIn();
const user = JSON.parse(localStorage.getItem('user') || '{}');

// Server-side: only the API can clear an httpOnly cookie, and it also bumps
// tokenVersion so the token is dead even if it was captured beforehand.
const handleLogout = () => { logout(); };

// Cache the 2FA gate check for the session so navigation doesn't re-fetch it.
let mustSetUp2FA: boolean | null = null;

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const [checked, setChecked] = useState(mustSetUp2FA !== null);
  const [gate, setGate] = useState(mustSetUp2FA === true);

  useEffect(() => {
    if (!isAuthenticated() || mustSetUp2FA !== null) return;
    apiFetch("/api/2fa/status")
      .then((res) => (res?.ok ? res.json() : null))
      .then((data) => {
        mustSetUp2FA = !!data?.mustSetUp;
        setGate(mustSetUp2FA);
        setChecked(true);
      })
      .catch(() => setChecked(true));
  }, []);

  if (!isAuthenticated()) return <Navigate to="/login" replace />;
  if (!checked) return null;
  if (gate) {
    return (
      <TwoFactorSetup
        forced
        onDone={() => {
          mustSetUp2FA = false;
          window.location.href = "/";
        }}
      />
    );
  }
  return <>{children}</>;
};

/**
 * RestaurantProvider used to wrap the entire <Router>, including public
 * routes like /qr/:code and /privacy that a guest with no account ever
 * reaches. Its billing-refresh effect only fires when isSignedIn() is true,
 * but that flag just means "this browser logged in at some point," not that
 * the session is still valid. An admin whose 1-hour session had quietly
 * expired, opening a QR link to test it in the same browser, still tripped
 * the flag: the refresh fired, drew a real 401, and apiFetch's 401 handler
 * redirected the guest page to /login (found 2026-09-14, Fred's first live
 * QR test).
 *
 * Fixed as a pathless layout route wrapping only the admin paths below,
 * rather than wrapping RestaurantProvider around each route's element
 * individually, since a per-route wrapper would remount the provider (and
 * refetch restaurants/billing, with a loading flash) on every single
 * navigation between two admin pages, since only one matched route's
 * element exists at a time. A layout route's element stays mounted across
 * navigation between its children; only the <Outlet /> content swaps.
 */
const AdminLayout: React.FC = () => (
  <RestaurantProvider>
    <Outlet />
  </RestaurantProvider>
);

// The route's own `element` is decided in App's render, which is above
// RestaurantProvider in the tree, so useRestaurant() isn't reachable there.
// This tiny wrapper IS rendered inside AdminLayout's <Outlet>, so it can
// check the manager's delegated permission (Fred, 2026-09-15) the same way
// the nav item does, rather than only hiding the link and leaving the route
// itself open to anyone who types the URL.
const CampaignAnalyticsRoute: React.FC = () => {
  const { canViewAnalytics } = useRestaurant();
  if (user.role !== 'admin' && !canViewAnalytics) return <Navigate to="/" replace />;
  return <CampaignAnalytics />;
};

// Superadmin functional components
const SuperDashboard: React.FC = () => {
  const { t } = useTranslation();
  const [platformStats, setPlatformStats] = useState<any>(null);
  const [systemHealth, setSystemHealth] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  // Without this the page rendered an empty dashboard when the request failed,
  // which reads as "you have no data" rather than "we could not load it".
  const [error, setError] = useState<string | null>(null);

  const fetchData = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // apiFetch redirects to /login on 401 itself, so there is no token check
      // to do here, since the session is an httpOnly cookie this code cannot see.
      const [statsResponse, healthResponse] = await Promise.all([
        apiFetch('/api/superadmin/platform-stats'),
        apiFetch('/api/superadmin/system-health'),
      ]);

      if (statsResponse?.ok) setPlatformStats(await statsResponse.json());
      else setError(t('superadmin.loadFailed'));

      if (healthResponse?.ok) setSystemHealth(await healthResponse.json());
      else setError(t('superadmin.loadFailed'));
    } catch {
      setError(t('superadmin.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">{t('superadmin.platformDashboard')}</h1>

      {error && (
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 bg-red-50 border border-red-200 text-red-800 rounded-lg p-4">
          <span>{error}</span>
          <button onClick={fetchData} className="px-3 py-1.5 bg-red-600 text-white rounded font-semibold hover:bg-red-700 transition">
            {t('common.retry')}
          </button>
        </div>
      )}

      {/* System Health */}
      {systemHealth && (
        <div className="mb-6">
          <div className={`p-4 rounded-lg border ${
            systemHealth.status === 'healthy' ? 'bg-green-50 border-green-200' :
            systemHealth.status === 'warning' ? 'bg-yellow-50 border-yellow-200' :
            'bg-red-50 border-red-200'
          }`}>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">{t('superadmin.systemHealth')}</h2>
              <span className={`px-2 py-1 rounded text-sm font-medium ${
                systemHealth.status === 'healthy' ? 'bg-green-100 text-green-800' :
                systemHealth.status === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              }`}>
                {/* The API returns a bare English word ("healthy"), which was
                    being uppercased straight onto the screen and so stayed
                    English on the French UI. The second argument is the
                    fallback for any status added to the API later. */}
                {t(`superadmin.health.${systemHealth.status}`, { defaultValue: systemHealth.status }).toUpperCase()}
              </span>
            </div>
            <div className="mt-2 text-sm text-gray-600">
              {t('superadmin.database')}:{' '}
              {systemHealth.database?.status
                ? t(`superadmin.health.${systemHealth.database.status}`, { defaultValue: systemHealth.database.status })
                : t('superadmin.unknown')}
              {systemHealth.database?.detail && (
                <span className="text-gray-400"> ({systemHealth.database.detail})</span>
              )} |
              {' '}{t('superadmin.issues')}: {systemHealth.issues?.totalIssues || 0}
            </div>
            {/* A badge that says "degraded" without saying what is degraded
                sends you hunting through logs. Each failing check names itself
                and carries the number or timing that triggered it. */}
            {systemHealth.checks?.some((c: any) => c.status !== 'healthy') && (
              <ul className="mt-3 space-y-1 text-sm">
                {systemHealth.checks
                  .filter((c: any) => c.status !== 'healthy')
                  .map((c: any) => (
                    <li key={c.id} className="flex items-start gap-2">
                      <span
                        className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${
                          c.status === 'error' ? 'bg-red-500' : 'bg-yellow-500'
                        }`}
                      />
                      <span className="text-gray-700">
                        {t(`superadmin.check.${c.id}`, { defaultValue: c.id })}
                        {c.detail && <span className="text-gray-500">: {c.detail}</span>}
                      </span>
                    </li>
                  ))}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Platform Overview */}
      {platformStats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">{t('superadmin.totalUsers')}</h3>
            <p className="text-2xl font-bold text-teal-600">{platformStats.overview.totalUsers}</p>
            <div className="text-xs text-gray-400 mt-1">
              {t('superadmin.adminsManagers', { admins: platformStats.overview.totalAdmins, managers: platformStats.overview.totalManagers })}
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">{t('superadmin.restaurants')}</h3>
            <p className="text-2xl font-bold text-green-600">{platformStats.overview.totalRestaurants}</p>
            <div className="text-xs text-gray-400 mt-1">
              {t('superadmin.activeLocations')}
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">{t('superadmin.reviews')}</h3>
            <p className="text-2xl font-bold text-purple-600">{platformStats.overview.totalReviews}</p>
            <div className="text-xs text-gray-400 mt-1">
              {t('superadmin.responseRate', { rate: platformStats.overview.responseRate })}
            </div>
          </div>

          <div className="bg-white p-4 rounded-lg shadow">
            <h3 className="text-sm font-medium text-gray-500">{t('superadmin.qrCampaigns')}</h3>
            <p className="text-2xl font-bold text-orange-600">{platformStats.overview.totalQRCodes}</p>
            <div className="text-xs text-gray-400 mt-1">
              {t('superadmin.scansOptIns', { scans: platformStats.overview.totalScans, optIns: platformStats.overview.totalOptIns })}
            </div>
          </div>
        </div>
      )}

      {/* Recent Activity */}
      {platformStats && (
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-semibold mb-4">{t('superadmin.recentActivity')}</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-gray-500">{t('superadmin.newReviews')}</p>
              <p className="text-xl font-bold text-teal-600">{platformStats.recentActivity.reviewsThisWeek}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('superadmin.replies')}</p>
              <p className="text-xl font-bold text-green-600">{platformStats.recentActivity.repliesThisWeek}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('superadmin.qrScans')}</p>
              <p className="text-xl font-bold text-purple-600">{platformStats.recentActivity.scansThisWeek}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('superadmin.optIns')}</p>
              <p className="text-xl font-bold text-orange-600">{platformStats.recentActivity.optInsThisWeek}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SuperRestaurants: React.FC = () => {
  const { t } = useTranslation();
  const [restaurants, setRestaurants] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  // An empty list and a failed request look identical without this.
  const [loadError, setLoadError] = useState(false);
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRestaurant, setSelectedRestaurant] = useState('all');

  useEffect(() => {
    const fetchRestaurants = async () => {
      try {
        const response = await apiFetch('/api/superadmin/restaurants');

        if (response?.ok) {
          const data = await response.json();
          setRestaurants(data);
        } else {
          setLoadError(true);
        }
      } catch (error) {
        setLoadError(true);
        console.error('Error fetching restaurants:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRestaurants();
  }, []);

  // Get unique restaurant names for filter dropdown
  const allRestaurantNames = Array.from(new Set(restaurants.map(r => r.name))).sort();

  // Filter restaurants based on search and dropdown
  const filteredRestaurants = restaurants.filter(r => {
    const matchesSearch = searchTerm === '' || r.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDropdown = selectedRestaurant === 'all' || r.name === selectedRestaurant;
    return matchesSearch && matchesDropdown;
  });

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 gap-4">
        <h1 className="text-2xl font-bold mb-2 md:mb-0">{t('superadmin.allRestaurants')}</h1>
        <div className="flex flex-wrap gap-3 items-center">
          <input
            type="text"
            placeholder={t('superadmin.searchRestaurants')}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 min-w-[200px]"
          />
          <select
            value={selectedRestaurant}
            onChange={e => setSelectedRestaurant(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value="all">{t('superadmin.allRestaurants')}</option>
            {allRestaurantNames.map(name => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
          {(searchTerm || selectedRestaurant !== 'all') && (
            <button
              onClick={() => { setSearchTerm(''); setSelectedRestaurant('all'); }}
              className="px-4 py-2 bg-stone-500 text-white rounded-md font-medium hover:bg-gray-600 transition"
            >
              {t('superadmin.clearFilters')}
            </button>
          )}
        </div>
      </div>
      <div className="mb-4 p-3 bg-stone-50 rounded-lg text-sm text-gray-600">
        {t('superadmin.showingRestaurants', { shown: filteredRestaurants.length, total: restaurants.length })}
        {searchTerm && <span> • {t('superadmin.searchTag', { term: searchTerm })}</span>}
        {selectedRestaurant !== 'all' && <span> • {t('superadmin.restaurantTag', { name: selectedRestaurant })}</span>}
      </div>
      {filteredRestaurants.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {loadError ? t('superadmin.loadFailed') : t('superadmin.noRestaurantsFound')}
          </h3>
          <p className="text-gray-500 mb-4">
            {restaurants.length === 0
              ? t('superadmin.noRestaurantsYet')
              : t('superadmin.adjustSearch')
            }
          </p>
          {(searchTerm || selectedRestaurant !== 'all') && (
            <button
              onClick={() => { setSearchTerm(''); setSelectedRestaurant('all'); }}
              className="px-4 py-2 bg-teal-600 text-white rounded-md font-medium hover:bg-teal-700 transition"
            >
              {t('superadmin.clearAllFilters')}
            </button>
          )}
        </div>
      ) : (
        <div className="grid gap-4">
          {filteredRestaurants.map((restaurant) => (
            <div key={restaurant.id} className="bg-white p-4 rounded-lg shadow">
              <div className="flex items-start justify-between">
                <div className="flex items-center space-x-4">
                  {restaurant.logoUrl && (
                    <img
                      src={apiAssetUrl(restaurant.logoUrl)}
                      alt={`${restaurant.name} logo`}
                      className="w-12 h-12 rounded object-cover"
                    />
                  )}
                  <div>
                    <h3 className="font-semibold text-lg">{restaurant.name}</h3>
                    <p className="text-gray-600 text-sm">{restaurant.address}</p>
                    <p className="text-gray-500 text-xs">{restaurant.cuisine}</p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-500">{t('common.owner')}</div>
                  <div className="font-medium">{restaurant.owner.name}</div>
                  <div className="text-xs text-gray-400">{restaurant.owner.email}</div>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                <div>
                  <div className="text-lg font-bold text-teal-600">{restaurant.stats.totalReviews}</div>
                  <div className="text-xs text-gray-500">{t('superadmin.reviews')}</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-green-600">{restaurant.stats.avgRating}</div>
                  <div className="text-xs text-gray-500">{t('superadmin.avgRating')}</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-purple-600">{restaurant.stats.responseRate}%</div>
                  <div className="text-xs text-gray-500">{t('superadmin.responseRateLabel')}</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-orange-600">{restaurant.stats.totalScans}</div>
                  <div className="text-xs text-gray-500">{t('superadmin.qrScans')}</div>
                </div>
              </div>
              {restaurant.managers.length > 0 && (
                <div className="mt-3 pt-3 border-t">
                  <div className="text-sm text-gray-500 mb-1">{t('superadmin.managers')}</div>
                  <div className="flex flex-wrap gap-2">
                    {restaurant.managers.map((manager: any) => (
                      <span key={manager.id} className="text-xs bg-teal-100 text-teal-800 px-2 py-1 rounded">
                        {manager.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * One person's card. Extracted unchanged from the grouped list so the account
 * tree and the flat list render identically - the two views differ in how
 * people are arranged, never in what a person looks like.
 */
/** The same billing wording as Billing and the Control Panel, in chip form. */
const BillingStateChip: React.FC<{ state?: string }> = ({ state }) => {
  const { t } = useTranslation();
  if (!state) return null;
  const tone = state === 'active' || state === 'trialing'
    ? 'bg-teal-100 text-teal-700'
    : state === 'past_due' ? 'bg-amber-100 text-amber-700'
    : 'bg-gray-200 text-gray-600';
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium whitespace-nowrap ${tone}`}>
      {t(`billing.state.${state === 'past_due_expired' ? 'trial_expired' : state}`, { defaultValue: state })}
    </span>
  );
};

const UserCard: React.FC<{
  user: any;
  resetStatus: Record<number, string>;
  onForceReset: (id: number) => void;
  onDelete?: (user: any) => void;
  hideRestaurants?: boolean;
}> = ({ user, resetStatus, onForceReset, onDelete, hideRestaurants }) => {
  const { t } = useTranslation();
  return (
    <div className="bg-white p-4 rounded-lg shadow">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-lg">{user.name}</h3>
          <p className="text-gray-600">{user.email}</p>
          <div className="flex items-center space-x-2 mt-1">
            <span className={`px-2 py-1 rounded text-xs font-medium ${
              user.role === 'admin' ? 'bg-teal-100 text-teal-800' :
              'bg-green-100 text-green-800'
            }`}>
              {t(`roles.${user.role}`, { defaultValue: user.role })}
            </span>
            <span className={`px-2 py-1 rounded text-xs ${
              user.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}>
              {user.isActive ? t('common.active') : t('common.inactive')}
            </span>
            {user.role === 'manager' && user.restaurants.length === 0 && (
              <span className="px-2 py-1 rounded text-xs font-medium bg-amber-100 text-amber-900">
                {t('superadmin.noLocation')}
              </span>
            )}
          </div>
        </div>

        <div className="text-right">
          <div className="text-sm text-gray-500">{t('superadmin.restaurantsLabel')}</div>
          <div className="font-medium">{user.stats.totalRestaurants}</div>
          <div className="text-xs text-gray-400">{t('superadmin.repliesCount', { count: user.stats.replyCount })}</div>
        </div>
      </div>

      {!hideRestaurants && user.restaurants.length > 0 && (
        <div className="mt-3 pt-3 border-t">
          <div className="text-sm text-gray-500 mb-1">{t('superadmin.restaurantsColon')}</div>
          <div className="flex flex-wrap gap-2">
            {user.restaurants.map((restaurant: any) => (
              <span key={restaurant.id} className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded">
                {restaurant.name}
              </span>
            ))}
          </div>
        </div>
      )}
  
      <div className="mt-3 pt-3 border-t flex items-center justify-between">
        <div className="text-xs text-gray-500">
          {t('superadmin.joined', { date: new Date(user.createdAt).toLocaleDateString() })}
          {user.stats.lastReply && (
            <span className="ml-4">
              {t('superadmin.lastReply', { date: new Date(user.stats.lastReply).toLocaleDateString() })}
            </span>
          )}
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => onForceReset(user.id)}
            className="bg-red-600 text-white px-3 py-1 rounded text-sm hover:bg-red-700 transition"
          >
            {t('superadmin.forceReset')}
          </button>
          {onDelete && user.role === 'manager' && (
            <button
              onClick={() => onDelete(user)}
              className="bg-white text-red-700 border border-red-300 px-3 py-1 rounded text-sm hover:bg-red-50 transition"
            >
              {t('users.delete')}
            </button>
          )}
        </div>
      </div>
  
      {resetStatus[user.id] && (
        <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-sm">
          {resetStatus[user.id]}
        </div>
      )}
    </div>
  );
};

const SuperUsers: React.FC = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  // An empty list and a failed request look identical without this.
  const [loadError, setLoadError] = useState(false);
  const [resetStatus, setResetStatus] = useState<{ [key: number]: string }>({});
  const [deletingUser, setDeletingUser] = useState<any | null>(null);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRestaurant, setSelectedRestaurant] = useState<string>('all');
  const [selectedRole, setSelectedRole] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [groupByAccount, setGroupByAccount] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await apiFetch('/api/superadmin/users');

        if (response?.ok) {
          const data = await response.json();
          setUsers(data);
        } else {
          setLoadError(true);
        }
      } catch (error) {
        setLoadError(true);
        console.error('Error fetching users:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  // Get unique restaurants for filter dropdown
  const allRestaurants = Array.from(new Set(
    users.flatMap(user => user.restaurants.map((r: any) => r.name))
  )).sort();

  // Filter users based on search and filters
  const filteredUsers = users.filter(user => {
    const matchesSearch = searchTerm === '' || 
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesRestaurant = selectedRestaurant === 'all' || 
      user.restaurants.some((r: any) => r.name === selectedRestaurant);
    
    const matchesRole = selectedRole === 'all' || user.role === selectedRole;
    
    const matchesStatus = selectedStatus === 'all' || 
      (selectedStatus === 'active' && user.isActive) ||
      (selectedStatus === 'inactive' && !user.isActive);
    
    return matchesSearch && matchesRestaurant && matchesRole && matchesStatus;
  });

  // Group by account, not by restaurant.
  //
  // Grouping by restaurant put the same multi-location admin into one group per
  // location they own, so one person appeared several times with nothing saying
  // it was the same person. The account is the real unit here: one admin, who
  // pays, owns the locations and hires the managers.
  //
  // Managers hang off a *location*, never off the account directly - that is
  // Fred's isolation invariant, and showing them under the account would teach
  // the wrong model. A manager belongs to exactly one admin
  // (LOCATION_ISOLATION_PLAN.md section 9), so every manager has exactly one
  // place in this tree.
  const accounts = React.useMemo(() => {
    const byAdmin = new Map<number, any>();
    const locationIndex = new Map<number, any>();

    for (const admin of users.filter((u) => u.role === 'admin')) {
      const locations = admin.restaurants.map((r: any) => ({ ...r, managers: [] as any[] }));
      locations.forEach((l: any) => locationIndex.set(l.id, l));
      byAdmin.set(admin.id, { admin, locations });
    }

    // Anyone we cannot place: a manager whose restaurant was deleted, or one
    // owned by an admin the filter excluded. Surfaced rather than dropped -
    // silently vanishing users is how orphaned data goes unnoticed.
    const unplaced: any[] = [];
    for (const person of users.filter((u) => u.role !== 'admin')) {
      const location = locationIndex.get(person.restaurants[0]?.id);
      if (location) location.managers.push(person);
      else unplaced.push(person);
    }

    return { list: Array.from(byAdmin.values()), unplaced };
  }, [users]);

  // The tree is built from every user so the account and location headings
  // survive a filter that only matches a manager; the filter then decides which
  // cards render, and empty accounts drop out.
  const visibleIds = new Set(filteredUsers.map((u) => u.id));

  const handleForceReset = async (userId: number) => {
    if (window.confirm(t('superadmin.forceResetConfirm'))) {
      try {
        const response = await apiFetch(`/api/superadmin/users/${userId}/force-reset`, {
          method: 'POST'
        });
        
        if (response?.ok) {
          const result = await response.json();
          setResetStatus(prev => ({
            ...prev,
            [userId]: result.emailed ? t('superadmin.passwordResetEmailed') : t('superadmin.passwordResetEmailFailed')
          }));
          
          // Clear status after 10 seconds
          setTimeout(() => {
            setResetStatus(prev => {
              const newStatus = { ...prev };
              delete newStatus[userId];
              return newStatus;
            });
          }, 10000);
        }
      } catch (error) {
        console.error('Error resetting password:', error);
        setResetStatus(prev => ({
          ...prev,
          [userId]: t('superadmin.errorResettingPassword')
        }));
      }
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
        <h1 className="text-2xl font-bold mb-4 lg:mb-0">{t('superadmin.allUsers')}</h1>

        {/* Filter Controls */}
        <div className="flex flex-wrap gap-3">
          {/* Search */}
          <div className="flex-1 min-w-[200px]">
            <input
              type="text"
              placeholder={t('superadmin.searchUsers')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>

          {/* Restaurant Filter */}
          <select
            value={selectedRestaurant}
            onChange={(e) => setSelectedRestaurant(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value="all">{t('superadmin.allRestaurants')}</option>
            {allRestaurants.map(restaurant => (
              <option key={restaurant} value={restaurant}>{restaurant}</option>
            ))}
          </select>

          {/* Role Filter */}
          <select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value="all">{t('superadmin.allRoles')}</option>
            <option value="admin">{t('superadmin.admin')}</option>
            <option value="manager">{t('superadmin.manager')}</option>
          </select>

          {/* Status Filter */}
          <select
            value={selectedStatus}
            onChange={(e) => setSelectedStatus(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
          >
            <option value="all">{t('superadmin.allStatus')}</option>
            <option value="active">{t('common.active')}</option>
            <option value="inactive">{t('common.inactive')}</option>
          </select>

          {/* Group Toggle */}
          <button
            onClick={() => setGroupByAccount(!groupByAccount)}
            className={`px-4 py-2 rounded-md font-medium transition ${
              groupByAccount
                ? 'bg-teal-600 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {groupByAccount ? t('superadmin.ungroup') : t('superadmin.groupByAccount')}
          </button>

          {/* Clear Filters */}
          {(searchTerm || selectedRestaurant !== 'all' || selectedRole !== 'all' || selectedStatus !== 'all') && (
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedRestaurant('all');
                setSelectedRole('all');
                setSelectedStatus('all');
              }}
              className="px-4 py-2 bg-stone-500 text-white rounded-md font-medium hover:bg-gray-600 transition"
            >
              {t('superadmin.clearFilters')}
            </button>
          )}
        </div>
      </div>

      {/* Results Summary */}
      <div className="mb-4 p-3 bg-stone-50 rounded-lg">
        <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600">
          <span>{t('superadmin.showingUsers', { shown: filteredUsers.length, total: users.length })}</span>
          {searchTerm && <span>• {t('superadmin.searchTag', { term: searchTerm })}</span>}
          {selectedRestaurant !== 'all' && <span>• {t('superadmin.restaurantTag', { name: selectedRestaurant })}</span>}
          {selectedRole !== 'all' && <span>• {t('superadmin.roleTag', { role: selectedRole })}</span>}
          {selectedStatus !== 'all' && <span>• {t('superadmin.statusTag', { status: selectedStatus })}</span>}
          {groupByAccount && <span>• {t('superadmin.groupedByAccount')}</span>}
        </div>
      </div>
      
      {/* Users Display */}
      {filteredUsers.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {loadError ? t('superadmin.loadFailed') : t('superadmin.noUsersFound')}
          </h3>
          <p className="text-gray-500 mb-4">
            {loadError
              ? t('superadmin.loadFailedHint')
              : users.length === 0
              ? t('superadmin.noUsersYet')
              : t('superadmin.adjustSearch')
            }
          </p>
          {(searchTerm || selectedRestaurant !== 'all' || selectedRole !== 'all' || selectedStatus !== 'all') && (
            <button
              onClick={() => {
                setSearchTerm('');
                setSelectedRestaurant('all');
                setSelectedRole('all');
                setSelectedStatus('all');
              }}
              className="px-4 py-2 bg-teal-600 text-white rounded-md font-medium hover:bg-teal-700 transition"
            >
              {t('superadmin.clearAllFilters')}
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          {!groupByAccount ? (
            <div className="grid gap-4">
              {filteredUsers.map((user: any) => (
                <UserCard key={user.id} user={user} resetStatus={resetStatus} onForceReset={handleForceReset} onDelete={setDeletingUser} />
              ))}
            </div>
          ) : (
            <>
              {accounts.list.map(({ admin, locations }: any) => {
                const shownAdmin = visibleIds.has(admin.id) ? admin : null;
                const shownLocations = locations
                  .map((l: any) => ({ ...l, managers: l.managers.filter((m: any) => visibleIds.has(m.id)) }))
                  .filter((l: any) => l.managers.length > 0 || shownAdmin);
                if (!shownAdmin && shownLocations.length === 0) return null;

                const people = (shownAdmin ? 1 : 0) +
                  shownLocations.reduce((n: number, l: any) => n + l.managers.length, 0);
                // One location needs no sub-heading: its name goes in the
                // account header and the managers sit directly underneath. The
                // ordinary customer is one admin, one restaurant, one manager,
                // and they should not be reading a three-level tree to find it.
                const single = locations.length === 1;

                return (
                  <div key={admin.id} className="bg-stone-50 rounded-xl p-4">
                    <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 mb-3">
                      <h2 className="text-lg font-semibold text-gray-800">
                        {admin.name || admin.email}
                        {single && locations[0] && (
                          <span className="ml-2 text-sm font-normal text-gray-500">{locations[0].name}</span>
                        )}
                      </h2>
                      <div className="flex items-center gap-2 text-sm text-gray-500">
                        {single && locations[0]
                          ? <BillingStateChip state={locations[0].state} />
                          : <span>{t('superadmin.locationCount', { count: locations.length })}</span>}
                        <span>·</span>
                        <span>{t('superadmin.peopleCount', { count: people })}</span>
                      </div>
                    </div>

                    <div className="grid gap-4">
                      {shownAdmin && (
                        <UserCard user={shownAdmin} resetStatus={resetStatus}
                                  onForceReset={handleForceReset} onDelete={setDeletingUser} hideRestaurants={single} />
                      )}

                      {single
                        ? shownLocations[0]?.managers.map((m: any) => (
                            <UserCard key={m.id} user={m} resetStatus={resetStatus}
                                      onForceReset={handleForceReset} onDelete={setDeletingUser} hideRestaurants />
                          ))
                        : shownLocations.map((l: any) => (
                            <div key={l.id}>
                              <div className="flex items-center gap-2 mb-2 pl-1">
                                <span className="text-sm font-medium text-gray-700">{l.name}</span>
                                <BillingStateChip state={l.state} />
                              </div>
                              {l.managers.length === 0 ? (
                                <p className="pl-1 text-sm text-gray-400">{t('superadmin.noManagers')}</p>
                              ) : (
                                <div className="grid gap-4">
                                  {l.managers.map((m: any) => (
                                    <UserCard key={m.id} user={m} resetStatus={resetStatus}
                                              onForceReset={handleForceReset} onDelete={setDeletingUser} hideRestaurants />
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                    </div>
                  </div>
                );
              })}

              {/* Never silently dropped: a manager we cannot place means either
                  a deleted restaurant or a filtered-out owner, and both are
                  worth seeing. */}
              {accounts.unplaced.filter((u: any) => visibleIds.has(u.id)).length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <h2 className="text-lg font-semibold text-amber-900 mb-3">{t('superadmin.unplacedUsers')}</h2>
                  <div className="grid gap-4">
                    {accounts.unplaced.filter((u: any) => visibleIds.has(u.id)).map((u: any) => (
                      <UserCard key={u.id} user={u} resetStatus={resetStatus} onForceReset={handleForceReset} onDelete={setDeletingUser} />
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
      {deletingUser && (
        <DeleteManagerModal
          user={deletingUser}
          onDeleted={(id) => setUsers((list) => list.filter((u) => u.id !== id))}
          onClose={() => setDeletingUser(null)}
          stillAt={deletingUser.restaurants.map((r: any) => r.name)}
        />
      )}
    </div>
  );
};

// Redirect superadmin to /dashboard from / route
const SuperadminRedirect: React.FC = () => {
  if (user.role === 'superadmin') {
    return <Navigate to="/dashboard" replace />;
  }
  return <Reviews />;
};
  
  // `primary` marks the items that appear in the mobile bottom tab bar (the
  // most frequently used destinations, capped at 4 to avoid the crowding we
  // hit before); everything else is drawer-only on mobile. Desktop's sidebar
  // ignores the flag and always shows the full list, since there's no space constraint there.
  const navLinks = user.role === 'superadmin'
    ? [
      { to: "/dashboard", labelKey: "nav.dashboard", icon: <ChartBarIcon className="w-6 h-6" />, show: true, primary: true },
        // Drawer, not the bottom bar: superadmin had five primary items against
        // the four the bar is meant to hold, and the Control Panel is a screen
        // you go to deliberately rather than tab between.
        { to: "/master", labelKey: "nav.master", icon: <Squares2X2Icon className="w-6 h-6" />, show: true, primary: false },
        { to: "/restaurants", labelKey: "nav.restaurants", icon: <BuildingStorefrontIcon className="w-6 h-6" />, show: true, primary: true },
        { to: "/users", labelKey: "nav.users", icon: <UsersIcon className="w-6 h-6" />, show: true, primary: true },
        { to: "/signup-requests", labelKey: "nav.signupRequests", icon: <InboxStackIcon className="w-6 h-6" />, show: true, primary: false },
        { to: "/audit-log", labelKey: "nav.auditLog", icon: <ShieldCheckIcon className="w-6 h-6" />, show: true, primary: false },
        { to: "/ai-usage", labelKey: "nav.aiUsage", icon: <SparklesIcon className="w-6 h-6" />, show: true, primary: false },
        { to: "/profile", labelKey: "nav.profile", icon: <UserPlusIcon className="w-6 h-6" />, show: true, primary: true },
      ]
    : [
        { to: "/", labelKey: "nav.reviews", icon: <HomeIcon className="w-6 h-6" />, show: true, primary: true },
      { to: "/stats", labelKey: "nav.stats", icon: <ChartBarIcon className="w-6 h-6" />, show: true, primary: true },
        { to: "/users", labelKey: "nav.users", icon: <UsersIcon className="w-6 h-6" />, show: user.role === 'admin' || user.role === 'superadmin', primary: true },
        { to: "/restaurant", labelKey: "nav.restaurant", icon: <BuildingStorefrontIcon className="w-6 h-6" />, show: user.role === 'admin', primary: true },
        { to: "/invite", labelKey: "nav.invites", icon: <UserPlusIcon className="w-6 h-6" />, show: user.role === 'admin', primary: false },
        { to: "/billing", labelKey: "nav.billing", icon: <CreditCardIcon className="w-6 h-6" />, show: user.role === 'admin', primary: false },
        { to: "/campaign-analytics", labelKey: "nav.campaignAnalytics", icon: <QrCodeIcon className="w-6 h-6" />, show: user.role === 'admin', primary: false },
      { to: "/profile", labelKey: "nav.profile", icon: <UsersIcon className="w-6 h-6" />, show: true, primary: false },
        { to: "/help", labelKey: "help.navLabel", icon: <QuestionMarkCircleIcon className="w-6 h-6" />, show: true, primary: false },
      ];

const AdaptiveNav: React.FC<{ mobileOpen: boolean; onClose: () => void }> = ({ mobileOpen, onClose }) => {
  const location = useLocation();
  const { t } = useTranslation();
  // navLinks is computed once at module load from the localStorage snapshot
  // of `user`, so it can't itself react to a manager's delegated
  // permissions. Only this component, rendered inside RestaurantProvider,
  // can. Campaign Analytics and Restaurant are the two items whose
  // visibility now depends on more than role (Fred, 2026-09-15: delegated
  // permissions; canOrderCards added 2026-09-17). A manager delegated any
  // of canManageQr, canEditLandingPage or canOrderCards needs a way to
  // reach the Restaurant page's QR/landing/card-order accordions, even
  // though the rest of that page stays admin-only.
  const { canManageQr, canEditLandingPage, canViewAnalytics, canOrderCards } = useRestaurant();
  // G7c: unanswered reviews that arrived since the Reviews page was last opened.
  const newCount = useNewReviewCount(user.role === 'admin' || user.role === 'manager');
  const newBadge = (to: string, floating: boolean) =>
    to === '/' && newCount > 0 ? (
      <span
        className={`${floating ? 'absolute -top-1 -right-2' : 'ml-auto'} min-w-[1.25rem] h-5 px-1 rounded-full bg-red-600 text-white text-xs font-bold flex items-center justify-center`}
        aria-label={t('reviews.newCount', { count: newCount })}
      >
        {newCount > 99 ? '99+' : newCount}
      </span>
    ) : null;
  const visibleNavLinks = navLinks.map((l) => {
    if (l.to === '/campaign-analytics') return { ...l, show: user.role === 'admin' || canViewAnalytics };
    if (l.to === '/restaurant') return { ...l, show: user.role === 'admin' || canManageQr || canEditLandingPage || canOrderCards };
    return l;
  });
  return (
    <>
      {/* Desktop sidebar */}
      <div className="hidden md:flex flex-col h-screen w-56 bg-white border-r shadow-sm fixed top-0 left-0 z-20">
        <div className="p-4 text-xl font-bold text-teal-600 border-b">ReviewCounter</div>
        <RestaurantSwitcher />
        <nav className="flex-1 p-2 space-y-1">
          {visibleNavLinks.filter(l => l.show).map(link => (
            <Link
              key={link.to}
              to={link.to}
              className={`flex items-center gap-2 px-4 py-2 rounded transition font-medium ${location.pathname === link.to ? 'bg-teal-100 text-teal-700' : 'text-gray-700 hover:bg-gray-100'}`}
            >
              {link.icon}
              {t(link.labelKey)}
              {newBadge(link.to, false)}
            </Link>
          ))}
        </nav>
        <div className="p-4 border-t space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">{t('common.language')}</span>
            <LanguageToggle />
          </div>
          <button
            onClick={handleLogout}
            className="w-full text-sm bg-red-600 text-white py-2 rounded shadow hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-opacity-50 transition"
          >
            {t('common.logout')}
          </button>
        </div>
        <footer className="p-4 text-xs text-gray-400 border-t text-center">
          &copy; {new Date().getFullYear()} ReviewCounter
          <br />
          {t('common.poweredBy')}{' '}
          <a href="https://fredmichel.ca" target="_blank" rel="noopener noreferrer" className="font-semibold text-gray-600 hover:underline">
            FME Inc.
          </a>
        </footer>
      </div>

      {/* Mobile: dual nav. A bottom tab bar alone crowded badly past ~5 items
          (confirmed at 7 on an iPhone 12 Pro Max); a drawer alone meant even
          the most frequent pages needed an extra tap. Split instead: the
          `primary` items (Reviews/Stats/Users/Restaurant, capped at 4) live
          in a bottom bar for one-tap access, everything else (Invites,
          Profile, Help) plus Language/Logout lives in the drawer, opened by
          the hamburger in TopBar. */}
      <nav className="fixed bottom-0 left-0 right-0 z-30 bg-white border-t shadow flex md:hidden justify-around py-2">
        {visibleNavLinks.filter(l => l.show && l.primary).map(link => (
          <Link
            key={link.to}
            to={link.to}
            className={`flex flex-col items-center text-xs ${location.pathname === link.to ? 'text-teal-600' : 'text-gray-500'}`}
          >
            <span className="relative">
              {link.icon}
              {newBadge(link.to, true)}
            </span>
            {t(link.labelKey)}
          </Link>
        ))}
      </nav>

      <div
        className={`fixed inset-0 bg-black/40 z-40 md:hidden transition-opacity ${mobileOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className={`fixed top-0 left-0 h-full w-64 bg-white shadow-lg z-50 flex flex-col transform transition-transform md:hidden ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}`}
      >
        <div className="p-4 flex items-center justify-between border-b">
          <span className="text-xl font-bold text-teal-600">ReviewCounter</span>
          <button onClick={onClose} aria-label={t('common.close')} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>
        <RestaurantSwitcher />
        <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
          {visibleNavLinks.filter(l => l.show && !l.primary).map(link => (
            <Link
              key={link.to}
              to={link.to}
              onClick={onClose}
              className={`flex items-center gap-2 px-4 py-2 rounded transition font-medium ${location.pathname === link.to ? 'bg-teal-100 text-teal-700' : 'text-gray-700 hover:bg-gray-100'}`}
            >
              {link.icon}
              {t(link.labelKey)}
              {newBadge(link.to, false)}
            </Link>
          ))}
        </nav>
        <div className="p-4 border-t space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-500">{t('common.language')}</span>
            <LanguageToggle />
          </div>
          <button
            onClick={handleLogout}
            className="w-full text-sm bg-red-600 text-white py-2 rounded shadow hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-400 focus:ring-opacity-50 transition"
          >
            {t('common.logout')}
          </button>
        </div>
      </div>
    </>
  );
};

const TopBar: React.FC<{ onMenuClick: () => void; standalone: boolean; onRefresh: () => void }> = ({ onMenuClick, standalone, onRefresh }) => {
  const { t } = useTranslation();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  let displayName = user.name || user.email || "User";
  if (user.role === 'superadmin') {
    displayName = user.name || 'SA';
  }
  return (
    <header className="sticky top-0 bg-white shadow z-30 p-4 flex items-center justify-between gap-3 h-16">
      <div className="flex items-center gap-2">
        <button
          onClick={onMenuClick}
          aria-label={t('common.menu')}
          className="md:hidden text-gray-500 hover:text-gray-700 p-1.5 -ml-1.5 rounded hover:bg-gray-100 transition"
        >
          <Bars3Icon className="w-6 h-6" />
        </button>
        {/* Square icon, not the wordmark: this slot is ~48px wide and a
            580x204 lockup renders unreadable at that size. */}
        <img
          src="/reviewcounter-icon.png"
          alt="ReviewCounter"
          className="h-9 w-auto object-contain"
        />
      </div>
      <div className="flex items-center gap-2">
        <span className="text-gray-700 font-medium text-sm">
          {displayName}
          {user.role && (
            <span className="ml-2 px-2 py-0.5 rounded bg-teal-100 text-teal-600 text-xs uppercase">
              {t(`roles.${user.role}`, { defaultValue: user.role })}
            </span>
          )}
        </span>
        {/* Installed to a home screen there is no address bar and no
            pull-to-refresh, so this is the only way to reload. Shown only in
            standalone mode - in a browser tab it would duplicate a control the
            browser already provides. */}
        {standalone && (
          <button
            onClick={onRefresh}
            aria-label={t('common.refresh')}
            title={t('common.refresh')}
            className="text-gray-500 hover:text-gray-700 p-1.5 rounded hover:bg-gray-100 transition"
          >
            <ArrowPathIcon className="w-5 h-5" />
          </button>
        )}
      </div>
    </header>
  );
};

const MainLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { t } = useTranslation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const { updateReady, reload } = useAppUpdate();
  // Evaluated once: display-mode does not change during a session.
  const [standalone] = useState(isStandalone);

  return (
    <div className="flex min-h-screen flex-col">
      {/* Sits above everything, including the sticky header, because it is
          worth interrupting for: without it an installed app keeps running the
          old build until it is force-closed. */}
      {updateReady && (
        <div className="sticky top-0 z-40 bg-teal-600 text-white px-4 py-2 flex items-center justify-between gap-3 text-sm">
          <span>{t('common.updateAvailable')}</span>
          <button
            onClick={reload}
            className="shrink-0 px-3 py-1 bg-white text-teal-700 rounded font-semibold hover:bg-teal-50 transition"
          >
            {t('common.reload')}
          </button>
        </div>
      )}
      <TopBar onMenuClick={() => setMobileNavOpen(true)} standalone={standalone} onRefresh={reload} />
      {/* Below the header so it sits with the content it describes, and above
          the nav so it is on every page rather than only the one that failed. */}
      <ReadOnlyBanner />
      <GoogleAccessBanner />
      <div className="flex flex-1">
        <AdaptiveNav mobileOpen={mobileNavOpen} onClose={() => setMobileNavOpen(false)} />
        <div className="flex-1 md:ml-56 bg-stone-50 min-h-screen pb-16 md:pb-0">{children}</div>
      </div>
    </div>
  );
};

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/register" element={<Register />} />
        <Route path="/auth/callback" element={<GoogleAuthCallback />} />
        {/* Admin app: one shared RestaurantProvider instance for every
            route below, via the layout route's Outlet; see AdminLayout. */}
        <Route element={<AdminLayout />}>
        {/* Superadmin routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <MainLayout>
                {user.role === 'superadmin' ? <SuperDashboard /> : <Navigate to="/" replace />}
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/restaurants"
          element={
            <ProtectedRoute>
              <MainLayout>
                {user.role === 'superadmin' ? <SuperRestaurants /> : <Navigate to="/" replace />}
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/users"
          element={
            <ProtectedRoute>
              <MainLayout>
                {user.role === 'superadmin' ? <SuperUsers /> : <Users />}
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/master"
          element={
            <ProtectedRoute>
              <MainLayout>
                {user.role === 'superadmin' ? <MasterDashboard /> : <Navigate to="/" replace />}
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/billing"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Billing />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/audit-log"
          element={
            <ProtectedRoute>
              <MainLayout>
                {user.role === 'superadmin' ? <AuditLog /> : <Navigate to="/" replace />}
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/ai-usage"
          element={
            <ProtectedRoute>
              <MainLayout>
                {user.role === 'superadmin' ? <AiUsage /> : <Navigate to="/" replace />}
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/signup-requests"
          element={
            <ProtectedRoute>
              <MainLayout>
                {user.role === 'superadmin' ? <SignupRequests /> : <Navigate to="/" replace />}
              </MainLayout>
            </ProtectedRoute>
          }
        />
        {/* Regular user/admin routes */}
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <MainLayout>
                <SuperadminRedirect />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Profile />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/stats"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Stats />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/restaurant"
          element={
            <ProtectedRoute>
              <MainLayout>
                <Restaurant />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/campaign-analytics"
          element={
            <ProtectedRoute>
              <MainLayout>
                <CampaignAnalyticsRoute />
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/invite"
          element={
            <ProtectedRoute>
              <MainLayout>
                {user.role === 'admin' ? <Invites /> : <Navigate to="/" replace />}
              </MainLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/help"
          element={
            <ProtectedRoute>
              <MainLayout>
                {user.role === 'superadmin' ? <Navigate to="/dashboard" replace /> : <Help />}
              </MainLayout>
            </ProtectedRoute>
          }
        />
        </Route>
        <Route
          path="/accept-invite"
          element={<AcceptInvite />}
        />
        {/* QR Code Landing Page (public) */}
        <Route path="/qr/:code" element={<QRLanding />} />
        <Route path="/privacy" element={<Privacy />} />
        {/* Add more protected routes here */}
      </Routes>
    </Router>
  );
}

export default App;